import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ArrowRight, 
  BookOpen, 
  CheckCircle2, 
  Sparkles, 
  Shield, 
  Feather, 
  FileCheck, 
  Globe2, 
  Layers,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { PUBLISHING_STAGES_CONFIG } from '@ftt/shared';

export const HomePage: React.FC = () => {
  const { data: servicesRes } = useQuery({
    queryKey: ['public-services'],
    queryFn: () => api.get('/public/services').then((res) => res.data.data),
  });

  const { data: publicationsRes } = useQuery({
    queryKey: ['public-publications', { featured: 'true' }],
    queryFn: () => api.get('/public/publications?featured=true').then((res) => res.data.data),
  });

  const { data: testimonialsRes } = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get('/public/testimonials').then((res) => res.data.data),
  });

  const { data: newsRes } = useQuery({
    queryKey: ['public-news'],
    queryFn: () => api.get('/public/news').then((res) => res.data.data),
  });

  const services = servicesRes || [];
  const publications = publicationsRes || [];
  const testimonials = testimonialsRes || [];
  const news = newsRes || [];

  return (
    <div className="space-y-24 pb-20">
      
      {/* 1. REDESIGNED VISUAL HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-950 text-white pt-12 sm:pt-20 pb-16 sm:pb-20 border-b border-slate-800 font-sans">
        {/* Subtle background glow and radial dot pattern */}
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(#C29F78_1px,transparent_1px)] [background-size:28px_28px]"></div>
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Catchy Headline & Action Triggers */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-900/60 border border-brand-500/40 text-brand-300 text-xs font-mono tracking-widest uppercase">
                <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                <span>Premier Theological Publishing House</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-white">
                Publishing Literature That <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 via-brand-400 to-amber-200">Endures.</span>
              </h1>

              <p className="text-base sm:text-lg text-slate-300 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                From developmental line editing and classical typesetting to worldwide print and digital distribution. We partner with authors to turn deep conviction into timeless books.
              </p>

              {/* Catchy Value Chips */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5 text-xs font-medium text-slate-300 pt-1">
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/80 border border-slate-800 text-slate-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                  <span>100% Author Copyright Rights</span>
                </span>
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/80 border border-slate-800 text-slate-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                  <span>12-Stage Editorial Pathway</span>
                </span>
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/80 border border-slate-800 text-slate-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                  <span>Global Print &amp; Digital Reach</span>
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 pt-2">
                <Link
                  to="/submit-enquiry"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-sm shadow-lg shadow-brand-500/25 transition-all hover:scale-[1.02] active:scale-95"
                >
                  <span>Submit Your Manuscript</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                
                <Link
                  to="/our-process"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-850 text-slate-200 hover:text-white font-semibold text-sm border border-slate-800 transition-all active:scale-95"
                >
                  <span>Explore 12-Stage Process</span>
                </Link>
              </div>

              {/* Direct Quick Contact / Author Access */}
              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-5 text-xs text-slate-400">
                <Link to="/login" className="hover:text-brand-300 transition-colors">
                  Contracted Author Portal &rarr;
                </Link>
                <span>&bull;</span>
                <a href="tel:+918604685533" className="hover:text-brand-300 transition-colors font-medium">
                  Direct Line: +91 86046 85533
                </a>
              </div>

            </div>

            {/* Right Column: Visual Book & Milestone Showcase Card */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none">
                
                {/* Visual Glassmorphism Card */}
                <div className="p-6 sm:p-7 rounded-2xl bg-gradient-to-br from-slate-900/90 to-slate-950/90 border border-slate-800/90 shadow-2xl space-y-5 backdrop-blur-md">
                  
                  {/* Card Header Tag */}
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-300 font-bold">
                        Active Publishing Pipeline
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-brand-400 font-bold bg-brand-950 px-2 py-0.5 rounded border border-brand-800/40">
                      PRJ-2026-088
                    </span>
                  </div>

                  {/* Book Preview Mockup */}
                  <div className="flex gap-4 items-center">
                    <div className="w-24 sm:w-28 aspect-[3/4] rounded-lg overflow-hidden shadow-book border border-slate-700/80 bg-slate-900 shrink-0 transform -rotate-1 hover:rotate-0 transition-transform flex items-center justify-center p-1.5">
                      <img
                        src="https://forthetruth.in/wp-content/uploads/2023/06/The-Message-of-ACTS.png"
                        alt="The Message of Acts by John Stott"
                        className="max-h-full max-w-full object-contain drop-shadow-md"
                      />
                    </div>
                    <div className="space-y-1.5 overflow-hidden">
                      <span className="text-[9px] font-mono uppercase font-bold text-brand-400 block tracking-wider">
                        Biblical Studies &bull; Paperback
                      </span>
                      <h3 className="font-extrabold text-sm sm:text-base text-white tracking-tight leading-snug truncate">
                        The Message of Acts
                      </h3>
                      <p className="text-xs text-slate-400 truncate">
                        By John Stott
                      </p>
                      <div className="pt-1 flex items-center gap-2 text-[10px] text-emerald-400 font-mono font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>Available in Bookstore</span>
                      </div>
                    </div>
                  </div>

                  {/* Milestone Live Status Widget */}
                  <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800/80 space-y-2">
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-slate-400">Current Phase: Stage 8 of 12</span>
                      <span className="text-brand-400 font-bold">75% Complete</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-brand-500 h-1.5 rounded-full w-3/4"></div>
                    </div>
                    <span className="text-[10px] text-slate-500 block truncate">
                      Assigned: Senior Acquisitions Editor &bull; Press Date Set
                    </span>
                  </div>

                  {/* Fast Action CTA inside card */}
                  <div className="pt-1">
                    <Link
                      to="/submit-enquiry"
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-brand-600/20 hover:bg-brand-600 text-brand-300 hover:text-white border border-brand-500/40 text-xs font-bold transition-all"
                    >
                      <span>Submit Your Book for Review</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>

        {/* 4-Metric Trust Strip */}
        <div className="mt-14 pt-8 border-t border-slate-800/80 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">12 Stages</div>
              <div className="text-xs text-slate-400 font-medium">Transparent Editorial Roadmap</div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-extrabold text-brand-400 tracking-tight">100%</div>
              <div className="text-xs text-slate-400 font-medium">Author Copyright Retention</div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Global</div>
              <div className="text-xs text-slate-400 font-medium">Print &amp; Digital Distribution</div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-extrabold text-brand-400 tracking-tight">1-on-1</div>
              <div className="text-xs text-slate-400 font-medium">Senior Editorial Guidance</div>
            </div>
          </div>
        </div>

      </section>

      {/* 2. INTRODUCTION & PUBLISHING PHILOSOPHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-6 space-y-6">
            <div className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
              Who We Are &amp; What We Stand For
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark leading-tight">
              Publishing Literature That Honours Divine Truth &amp; Human Craftsmanship
            </h2>
            <p className="text-slate-600 leading-relaxed font-sans text-sm sm:text-base">
              At For The Truth Publishing, we believe that serious truth warrants serious care. We do not treat manuscripts as mere commodity content or rush assembly-line productions. 
            </p>
            <p className="text-slate-600 leading-relaxed font-sans text-sm sm:text-base">
              Every book entrusted to us undergoes rigorous developmental appraisal, painstaking line editing, bespoke typographical design, and durable physical production. Whether publishing theological monographs, expository commentaries, church history, or devotional works, our commitment remains uncompromising.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-white border border-editorial-border shadow-xs">
                <Shield className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Doctrinal Fidelity</h4>
                  <p className="text-xs text-slate-500 mt-1">Deep alignment with orthodox, classical biblical theology.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-white border border-editorial-border shadow-xs">
                <Feather className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Editorial Precision</h4>
                  <p className="text-xs text-slate-500 mt-1">Chicago Manual of Style rigor with respect for authorial voice.</p>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-brand-800"
              >
                <span>Read our full publishing philosophy</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative p-6 sm:p-10 rounded-2xl bg-gradient-to-br from-editorial-dark to-slate-900 text-white shadow-2xl border border-slate-800 space-y-6">
              <div className="w-12 h-12 rounded-lg bg-brand-500/20 border border-brand-500/40 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-brand-400" />
              </div>
              
              <h3 className="font-serif text-2xl font-bold text-slate-100">
                The For The Truth Publishing Standard
              </h3>

              <div className="space-y-4 text-xs sm:text-sm text-slate-300 divide-y divide-slate-800">
                <div className="pt-3 first:pt-0 flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                  <span><strong>Author Ownership:</strong> Authors retain creative dignity and direct consultation throughout all editorial passes.</span>
                </div>
                <div className="pt-3 flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                  <span><strong>Classical Typography:</strong> Custom interior typesetting, bespoke chapter openers, and balanced leading.</span>
                </div>
                <div className="pt-3 flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                  <span><strong>Full Production Control:</strong> Archival grade papers, foil stamping, cloth covers, and verified digital EPUBs.</span>
                </div>
                <div className="pt-3 flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                  <span><strong>Transparent Client Portal:</strong> Live milestone tracking, proof downloads, and direct editorial messaging.</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/submit-enquiry"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-semibold text-xs transition-colors"
                >
                  <span>Submit Manuscript for Review</span>
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 3. CORE PUBLISHING SERVICES */}
      <section className="bg-white py-20 border-y border-editorial-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
                Tailored Author Partnerships
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark">
                Comprehensive Publishing Services
              </h2>
              <p className="text-slate-600 text-sm sm:text-base">
                Each service is customized to your project stage, from initial diagnostic assessment to worldwide physical and digital distribution.
              </p>
            </div>

            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-brand-800 shrink-0"
            >
              <span>View All Services &amp; Details</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 4).map((service: any) => (
              <div
                key={service.id}
                className="group flex flex-col justify-between p-6 rounded-xl bg-editorial-parchment border border-editorial-border hover:border-brand-300 hover:shadow-editorial-lg transition-all"
              >
                <div className="space-y-4">
                  <div className="w-10 h-10 rounded-lg bg-white border border-editorial-border flex items-center justify-center group-hover:bg-brand-50 group-hover:border-brand-200 transition-colors">
                    <Sparkles className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-700">
                    {service.category}
                  </div>
                  <h3 className="font-serif text-lg font-bold text-slate-900 group-hover:text-brand-800 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {service.summary}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-200/80 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 font-medium">
                    Timeline: {service.timeline}
                  </span>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-xs font-bold text-brand-700 group-hover:text-brand-800 inline-flex items-center gap-1"
                  >
                    <span>Details</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 4. THE 12-STAGE PUBLISHING PROCESS ROADMAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center space-y-3 max-w-3xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Author Journey &amp; Milestones
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark">
            Our 12-Stage Publishing Lifecycle
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            From your first manuscript submission to lifelong catalog archiving, our step-by-step workflow guarantees total clarity and accountability.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {PUBLISHING_STAGES_CONFIG.map((stage) => (
            <div
              key={stage.status}
              className="p-5 rounded-xl bg-white border border-editorial-border shadow-xs hover:border-brand-300 transition-all flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="w-6 h-6 rounded-full bg-brand-100 text-brand-800 font-mono text-xs font-bold flex items-center justify-center">
                    {stage.order}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
                    Stage {stage.order} of 12
                  </span>
                </div>
                <h4 className="font-serif text-sm font-bold text-slate-900 pt-1">
                  {stage.label}
                </h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {stage.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center pt-4">
          <Link
            to="/our-process"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-editorial-dark text-white text-xs font-bold hover:bg-slate-800 transition-colors"
          >
            <span>Explore the Detailed 12-Stage Process</span>
            <ArrowRight className="w-3.5 h-3.5 text-brand-400" />
          </Link>
        </div>
      </section>

      {/* 5. FEATURED PUBLICATIONS / PORTFOLIO */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-400 font-bold">
                Selected Published Works
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-100">
                Crafted to Stand the Test of Time
              </h2>
              <p className="text-slate-400 text-sm sm:text-base">
                Discover a selection of recent volumes published under the imprint of For The Truth Publishing.
              </p>
            </div>

            <Link
              to="/publications"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-400 hover:text-brand-300 shrink-0"
            >
              <span>View Full Published Catalog</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {publications.map((book: any) => (
              <div
                key={book.id}
                className="group flex flex-col justify-between bg-slate-950 rounded-xl overflow-hidden border border-slate-800 hover:border-brand-500/50 shadow-xl transition-all"
              >
                <div className="aspect-[4/5] bg-slate-950 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-800">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-editorial-dark/90 backdrop-blur-xs text-brand-300 text-[10px] font-mono px-2.5 py-1 rounded uppercase tracking-wider font-bold border border-brand-500/30">
                    {book.format}
                  </div>
                </div>

                <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-2">
                    <span className="text-[11px] text-brand-400 font-mono font-medium block">
                      {book.category}
                    </span>
                    <h3 className="font-serif text-lg font-bold text-white group-hover:text-brand-300 transition-colors">
                      {book.title}
                    </h3>
                    <p className="text-xs text-slate-400 font-medium">
                      By {book.author}
                    </p>
                    <p className="text-xs text-slate-400 line-clamp-3 leading-relaxed pt-1">
                      {book.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-xs">
                    <Link
                      to={`/publications/${book.slug}`}
                      className="text-white hover:text-brand-400 font-bold"
                    >
                      Book Details &rarr;
                    </Link>
                    {book.bookstoreUrl && (
                      <a
                        href={book.bookstoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 font-semibold"
                      >
                        <span>Bookstore</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 6. TESTIMONIALS & AUTHOR EXPERIENCES */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-2 max-w-2xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
              Author Voices
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark">
              Partnership Built on Mutual Trust
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t: any) => (
              <div
                key={t.id}
                className="p-8 rounded-2xl bg-white border border-editorial-border shadow-sm space-y-6 flex flex-col justify-between"
              >
                <p className="font-serif italic text-slate-700 text-base sm:text-lg leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-200 border border-editorial-border">
                    <img src={t.avatarUrl} alt={t.clientName} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-900">{t.clientName}</h4>
                    <p className="text-xs text-slate-500">{t.authorTitle} &bull; <em>{t.bookTitle}</em></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 7. LATEST PUBLISHING NEWS & INSIGHTS */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
                Publishing Intelligence
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-editorial-dark">
                News &amp; Editorial Insights
              </h2>
            </div>
            <Link to="/news" className="text-xs font-bold text-brand-700 hover:text-brand-800">
              All Articles &rarr;
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {news.map((item: any) => (
              <Link
                key={item.id}
                to={`/news/${item.slug}`}
                className="p-6 rounded-xl bg-white border border-editorial-border hover:border-brand-400 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-2">
                  <span className="text-[10px] font-mono uppercase font-bold text-brand-600">
                    {item.category}
                  </span>
                  <h3 className="font-serif text-lg font-bold text-slate-900 group-hover:text-brand-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                    {item.excerpt}
                  </p>
                </div>
                <div className="pt-4 text-xs font-semibold text-brand-700 flex items-center gap-1">
                  <span>Read Article</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 8. HIGH IMPACT PUBLISHING ENQUIRY CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-editorial-dark text-white p-8 sm:p-14 border border-slate-800 text-center space-y-6">
          <div className="max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-400 font-bold">
              Ready to Publish?
            </span>
            <h2 className="text-3xl sm:text-5xl font-serif font-bold text-slate-100">
              Submit Your Proposal or Manuscript Today
            </h2>
            <p className="text-slate-300 text-sm sm:text-base font-light max-w-xl mx-auto leading-relaxed">
              Our acquisitions committee reviews every submission with theological diligence and editorial discernment. Receive a prompt, formal evaluation.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link
              to="/submit-enquiry"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-sm shadow-xl shadow-brand-500/20 transition-all hover:scale-[1.02]"
            >
              <span>Submit Publishing Enquiry (RFQ)</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 transition-all"
            >
              <span>Contact Editorial Department</span>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};
